import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = still checking, null = not logged in, object = logged in
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    // Probeer een paar keer: tijdens een (re)deploy kan de backend even 502/503 geven.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const u = await api.auth.me();
        setUser(u);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          // Echt ongeldige/verlopen sessie: pas dan uitloggen.
          clearToken();
          setUser(null);
          return;
        }
        // Tijdelijke fout (netwerk / 5xx tijdens deploy): token NIET wissen, even wachten en opnieuw proberen.
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    // Backend tijdelijk onbereikbaar: token behouden zodat je ingelogd blijft zodra hij terug is.
    setUser((prev) => (prev ? prev : null));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const data = await api.auth.login({ email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch {
      /* ignore */
    }
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
